import { useContext, useRef, type ReactNode } from "react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { cn } from "@/shared/lib/utils";
import { ChatWindowContext } from "./chat-window-context";

/** The reference's bottom sheet, contained within the floating chat. The
 * shadcn `DialogContent` always portals to `document.body`, which would sit
 * outside the phone panel's focus boundary, so the content element comes from
 * the Radix primitive while root, portal, overlay, title, description and
 * close are the shadcn wrappers. */
export function BottomSheet({
  title,
  description,
  onClose,
  children,
  footer,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const container = useContext(ChatWindowContext);
  const previousFocus = useRef(document.activeElement as HTMLElement | null);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPortal container={container}>
        <DialogOverlay className="absolute z-20 bg-black/60 data-[state=closed]:pointer-events-none" />
        <DialogPrimitive.Content
          className="absolute inset-x-0 bottom-0 z-[21] flex max-h-[88%] w-full flex-col rounded-t-2xl border border-b-0 border-border bg-card font-sans shadow-[0_25px_50px_rgb(0_0_0/25%)] outline-none"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (previousFocus.current?.isConnected)
              previousFocus.current.focus();
          }}
          onEscapeKeyDown={(event) => event.stopPropagation()}
        >
          <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/40" />
          <header className="flex items-start gap-2 px-4 pt-3 pb-2">
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-mono text-sm leading-normal font-semibold tracking-[0.12em] text-gold uppercase">
                {title}
              </DialogTitle>
              <DialogDescription
                className={cn("mt-1 text-xs", !description && "sr-only")}
              >
                {description || title}
              </DialogDescription>
            </div>
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Close ${title.toLowerCase()}`}
                className="min-h-11 min-w-11 cursor-pointer text-muted-foreground motion-reduce:transition-none"
              >
                <X />
              </Button>
            </DialogClose>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            {children}
          </div>
          {footer && (
            <footer className="shrink-0 border-t border-border px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
              {footer}
            </footer>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
